import type { UtteranceSource } from './types';

/**
 * The app's event vocabulary, per `specs/10-contracts.md`. `LearningService` (Phase 2)
 * and `DiagnosticsService` both subscribe.
 *
 * `DiagnosticsService` must strip `text` from `UtteranceSpoken` before persisting
 * anything (NFR7). That stripping lives in one place and is unit-tested; it is not
 * each subscriber's job to remember.
 */
export type AppEvent =
  /**
   * `msToAudio` is the tap-to-sound latency, and it is absent rather than zero when nothing can know
   * it — `visualOnly` makes no sound at all, and a provider that cannot say when playback began must
   * not be recorded as having begun it instantly (C80, `DIA-01`).
   *
   * It exists because a cold backend and a dead `AudioContext` are the same event from the outside:
   * a tap that appears to do nothing. C71 was closed against the wrong one of the two, twice, because
   * nothing in the app measured this.
   */
  | {
      type: 'UtteranceSpoken';
      text: string;
      source: UtteranceSource;
      providerId: string;
      msToAudio?: number;
    }
  | { type: 'KeyActivated'; key: string; latencyMs: number; position: { x: number; y: number } }
  | { type: 'ActivationMissed'; position: { x: number; y: number }; offset: { x: number; y: number } }
  | { type: 'PredictionAccepted'; slot: number }
  /**
   * A dwell that started and did not finish (3.1).
   *
   * Recorded because the *rate* is the only signal a caregiver has that `dwellMs` is wrong for today —
   * a device that logged only completed selections could not report a mode that is not working. `key`
   * is a target id, never text, so NFR7 is unaffected.
   */
  | { type: 'DwellCancelled'; key: string; reason: 'moved' | 'released' }
  /**
   * How many scan steps a selection cost (3.3: "measure scan cycles per selection and feed it to
   * diagnostics"). The number, not the target — it is what makes `scanIntervalMs` tunable from evidence.
   */
  | { type: 'ScanSelection'; steps: number }
  /**
   * Low-effort mode switched on or off (3.7: "diagnostics record when it is used, which tells you about fatigue
   * patterns over time"). Over months this is the only record of how often a bad day happens.
   */
  | { type: 'LowEffortToggled'; on: boolean }
  | { type: 'SpeechFailed'; providerId: string; reason: string }
  | { type: 'AudioContextLost' }
  /**
   * A repair of the audio path was attempted, and whether it worked (`SPK-19`).
   *
   * Whether it worked is the whole payload. The *count* is what a caregiver reads: a device that
   * needs repairing every morning has a defect this event cannot describe, and one that has never
   * needed it says the fix for C80 is holding.
   */
  | { type: 'AudioRepairAttempted'; ok: boolean };

export type AppEventType = AppEvent['type'];

type Listener = (event: AppEvent) => void;

const listeners = new Set<Listener>();

/**
 * Subscribe to every event. Returns the unsubscribe function.
 *
 * Deliberately synchronous and deliberately tiny: this sits on the < 50 ms
 * keypress path, so there is no queue, no async dispatch and no dependency.
 */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * A throwing subscriber must never break the emitter — a diagnostics bug cannot be
 * allowed to stop a keypress reaching the composer.
 */
export function emit(event: AppEvent): void {
  for (const listener of listeners) {
    try {
      listener(event);
    } catch (err) {
      console.error('[events] listener failed', event.type, err);
    }
  }
}

/** Test seam only. */
export function resetListeners(): void {
  listeners.clear();
}
